/**
 * @file: useCanRollbackOrder.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { useAppLoading } from '@euler/components/loading/Provider';
import { kDefaultMaxElapseTimeInHoursBeforeCanRollback } from '@euler/functions/constants';
import { useStoreConfiguration } from '@euler/functions/useStoreConfiguration';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { usePrompt } from '@euler/functions/ux/usePrompt';
import { HttpError } from '@euler/lib/request';
import { useServiceFactory } from '@euler/services/factory';
import { Maybe } from '@euler/types';
import { onErrorIgnore } from '@euler/utils';
import { dateFromValue } from '@euler/utils/datetime';
import { usePersistFn } from '@euler/utils/hooks';
import differenceInHours from 'date-fns/differenceInHours';
import { useCallback, useEffect, useState } from 'react';

export const useCanRollbackOrder = (
  params: {
    orderNo: string;
    taskNo: string;
    isMainOrder: boolean;
    finishedAt?: Maybe<string | Date>;
    allowed?: boolean;
  },
  callback?: (canRollback: boolean) => void,
) => {
  const { orderNo, taskNo, finishedAt, allowed = true } = params;

  const appLoading = useAppLoading();
  const orderContext = useOrderContext();
  const storeConfiguration = useStoreConfiguration();

  const { orderService } = useServiceFactory();

  const [canRollback, setCanRollback] = useState(() => {
    if (finishedAt == null || !allowed) return false;
    const maxElapseTimeInHours =
      storeConfiguration.maxElapseTimeInHoursBeforeCanRollback ??
      kDefaultMaxElapseTimeInHoursBeforeCanRollback;
    const elapsedInHours = differenceInHours(
      new Date(),
      dateFromValue(finishedAt),
    );
    return elapsedInHours >= 0 && elapsedInHours < maxElapseTimeInHours;
  });

  const callbackRef = usePersistFn((value: boolean) => {
    callback?.(value);
  });

  const check = useCallback(async () => {
    if (!allowed) return false;
    return await orderService.canRollbackFinished(orderNo).then(value => {
      setCanRollback(value);
      callbackRef(value);
      return value;
    });
  }, [allowed, callbackRef, orderNo, orderService]);

  const alert = useAlert();
  const prompt = usePrompt();
  const confirm = useConfirm();

  const rollback = usePersistFn(async () => {
    if (!allowed) return;

    const permitted = await appLoading.showWithAction(async () => {
      return await check();
    });

    const nonPermittedMsg =
      '对不起，该订单已完成较长时间，您当前不能进行进行返单操作。如您确实需要返单，可前往门店管理系统进行相关操作。如需任何帮助，请联系我们，感谢您的理解和支持!';

    if (!permitted) {
      void alert({ title: '提示', message: nonPermittedMsg });
      return;
    }

    const reason = await prompt({
      title: '返单原因',
      message: '为做好服务记录，请输入返单原因',
      destructive: true,
      required: true,
      confirmButtonText: '确认返单',
      cancelButtonText: '取消',
    });

    if (!reason?.trim()) {
      return;
    }

    if (
      !(await confirm({
        title: '再次确认是否继续返单?',
      }))
    ) {
      return;
    }

    await appLoading.showWithAction(async () => {
      try {
        await orderService.rollbackFinished(orderNo, {
          reason: reason.trim(),
        });
        orderContext.event$.next({
          type: 'rollback',
          orderNo,
          taskNo,
          isMainOrder: params.isMainOrder,
        });
      } catch (e) {
        const err = e as HttpError;
        if (
          err.subCode === 'revert_order_finished_before_max_allowed_threshold'
        ) {
          setCanRollback(false);
          void alert({ title: '出错了', message: nonPermittedMsg });
        } else {
          throw e;
        }
      }
    });
  });

  useEffect(() => {
    void check().catch(onErrorIgnore);
  }, [check]);

  return { canRollback, rollback, check };
};
